import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, EMPTY, tap } from 'rxjs';

import {
  GlobalSiteMasterStoreService,
  UpdateGlobalSiteNickname,
} from '@customer-portal/data-access/global';
import {
  DEFAULT_GRID_CONFIG,
  FilterableColumnDefinition,
  FilterOptions,
  GridConfig,
} from '@customer-portal/shared';
import { getFilterOptions } from '@customer-portal/shared/helpers';

import { SettingsSitesItemModel } from '../models';
import { SettingsSitesMapper } from '../services';
import { SettingsSiteNicknameService } from '../services/data-services/settings-site-nickname.service';
import {
  LoadSettingsSitesList,
  ResetSitesListState,
  SelectSiteForNickname,
  SubmitSiteNickname,
  SubmitSiteNicknameError,
  SubmitSiteNicknameSuccess,
  UpdateSitesFilterOptions,
  UpdateSitesGridConfig,
} from './actions';
import {
  SettingsCoBrowsingStoreService,
  SettingsCompanyDetailsStoreService,
} from './store-services';

export interface SitesStateModel {
  sitesList: SettingsSitesItemModel[];
  sitesGridConfig: GridConfig;
  sitesFilterOptions: FilterOptions;
  selectedSiteId: string | number | null;
  isSubmittingSiteNickname: boolean;
  siteNicknameError: string | null;
}

const defaultState: SitesStateModel = {
  sitesList: [],
  sitesGridConfig: DEFAULT_GRID_CONFIG,
  sitesFilterOptions: {},
  selectedSiteId: null,
  isSubmittingSiteNickname: false,
  siteNicknameError: null,
};

const COLUMNS_DEFINITION: FilterableColumnDefinition[] = [
  { field: 'siteName', hasColumnDelimiter: false },
  { field: 'addressLine1', hasColumnDelimiter: false },
  { field: 'countryName', hasColumnDelimiter: false },
  { field: 'city', hasColumnDelimiter: false },
  { field: 'siteZip', hasColumnDelimiter: false },
  { field: 'companyName', hasColumnDelimiter: false },
  { field: 'siteNickname', hasColumnDelimiter: false },
  { field: 'state', hasColumnDelimiter: false },
];

@State<SitesStateModel>({
  name: 'settingSites',
  defaults: defaultState,
})
@Injectable()
export class SettingsSitesState {
  private readonly globalSiteMasterStoreService = inject(
    GlobalSiteMasterStoreService,
  );
  private readonly settingsCompanyDetailsStoreService = inject(
    SettingsCompanyDetailsStoreService,
  );
  private readonly settingsCoBrowsingStoreService = inject(
    SettingsCoBrowsingStoreService,
  );
  private readonly settingsSiteNicknameService = inject(
    SettingsSiteNicknameService,
  );

  @Action(LoadSettingsSitesList)
  loadSettingsAdminList(ctx: StateContext<SitesStateModel>) {
    const siteMasterList = this.globalSiteMasterStoreService.siteMasterList();
    const isAdmin = this.settingsCompanyDetailsStoreService.isUserAdmin();
    const isDnvUser = this.settingsCoBrowsingStoreService.isDnvUser();
    const sitesList = SettingsSitesMapper.mapToSitesList(
      siteMasterList,
      isAdmin,
      isDnvUser,
    );
    ctx.patchState({
      sitesList,
    });
    ctx.dispatch(new UpdateSitesFilterOptions());
  }

  @Action(UpdateSitesGridConfig)
  updateSitesGridConfig(
    ctx: StateContext<SitesStateModel>,
    { sitesGridConfig }: UpdateSitesGridConfig,
  ): void {
    ctx.patchState({ sitesGridConfig });
    ctx.dispatch(new UpdateSitesFilterOptions());
  }

  @Action(UpdateSitesFilterOptions)
  updateSitesFilterOptions(ctx: StateContext<SitesStateModel>): void {
    const { sitesGridConfig, sitesList } = ctx.getState();

    const columnFilterConfigs: FilterableColumnDefinition[] =
      COLUMNS_DEFINITION;

    const filterOptions = getFilterOptions(
      sitesList,
      sitesGridConfig,
      columnFilterConfigs,
    );

    ctx.patchState({ sitesFilterOptions: filterOptions });
  }

  @Action(ResetSitesListState)
  resetSitesListState(ctx: StateContext<SitesStateModel>): void {
    ctx.setState(defaultState);
  }

  @Action(SelectSiteForNickname)
  selectSiteForNickname(
    ctx: StateContext<SitesStateModel>,
    { siteId }: SelectSiteForNickname,
  ): void {
    ctx.patchState({ selectedSiteId: siteId, siteNicknameError: null });
  }

  @Action(SubmitSiteNickname)
  submitSiteNickname(
    ctx: StateContext<SitesStateModel>,
    { payload }: SubmitSiteNickname,
  ) {
    const request = {
      siteId: payload.siteId,
      siteNickname: payload.siteNickname.trim(),
    };

    ctx.patchState({ isSubmittingSiteNickname: true, siteNicknameError: null });

    return this.settingsSiteNicknameService.saveSiteNickname(request).pipe(
      tap(() => {
        ctx.dispatch(new SubmitSiteNicknameSuccess(request));
      }),
      catchError((error: Error) => {
        ctx.dispatch(
          new SubmitSiteNicknameError(
            error?.message || 'Unable to update site nickname.',
          ),
        );

        return EMPTY;
      }),
    );
  }

  @Action(SubmitSiteNicknameSuccess)
  submitSiteNicknameSuccess(
    ctx: StateContext<SitesStateModel>,
    { payload }: SubmitSiteNicknameSuccess,
  ) {
    ctx.patchState({
      sitesList: ctx
        .getState()
        .sitesList.map((site) =>
          String(site.id) === String(payload.siteId)
            ? { ...site, siteNickname: payload.siteNickname }
            : site,
        ),
      siteNicknameError: null,
      isSubmittingSiteNickname: false,
    });

    ctx.dispatch(
      new UpdateGlobalSiteNickname(payload.siteId, payload.siteNickname),
    );
  }

  @Action(SubmitSiteNicknameError)
  submitSiteNicknameError(
    ctx: StateContext<SitesStateModel>,
    { error }: SubmitSiteNicknameError,
  ) {
    ctx.patchState({
      siteNicknameError: error,
      isSubmittingSiteNickname: false,
    });
  }
}
